import React, { useState, forwardRef } from 'react';
import { Icon, TooltipHost, mergeStyleSets } from '@fluentui/react';
import { DotsSixVertical, X as XIcon, PencilSimple } from '@phosphor-icons/react';
import ChartPanel from '../chat/ChartPanel';
import ErrorBoundary from '../ErrorBoundary';
import type { DashboardWidget, WidgetType } from '../../types/dashboard.types';
import type { ChartPanelChartType } from '../chat/ChartPanel';

/**
 * Format KPI values with the portal's dashboard conventions (see the governed
 * People Insights dashboard): full numbers with thousand separators below one
 * million (1,141 — never "1.1K"), one-decimal compaction only from millions up,
 * decimals capped at 1dp. A currency symbol is kept only when the value itself
 * arrives formatted (e.g. "$1,234.00" from a finance query).
 */
function formatCompact(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const str = String(value);
  const currencyMatch = str.match(/^([£€$¥])/);
  const prefix = currencyMatch ? currencyMatch[1] : '';
  const num = Number(str.replace(/[^0-9.\-eE]/g, ''));
  if (isNaN(num)) return str;
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}${prefix}${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000)     return `${sign}${prefix}${(abs / 1_000_000).toFixed(1)}M`;
  if (Number.isInteger(num)) return `${sign}${prefix}${abs.toLocaleString('en-AU')}`;
  return `${sign}${prefix}${abs.toLocaleString('en-AU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
}

/** Is a table cell value numeric? */
function isNumericValue(v: unknown): boolean {
  if (v === null || v === undefined || v === '') return false;
  return !isNaN(Number(String(v).replace(/[$,\s%]/g, '')));
}

// Extends HTMLAttributes so react-grid-layout's DraggableCore handlers
// (onMouseDown/onMouseUp/onTouchEnd) pass through to the root div — without
// this, widgets cannot be dragged.
interface Props extends React.HTMLAttributes<HTMLDivElement> {
  widget: DashboardWidget;
  onRemove: (id: string) => void;
  onResize: (id: string, size: 'small' | 'medium' | 'large') => void;
  onChangeChartType: (id: string, chartType: ChartPanelChartType) => void;
  onChangeWidgetType?: (id: string, newType: WidgetType) => void;
  onUpdateLegendLabels?: (id: string, legendLabels: Record<string, string>) => void;
  readOnly?: boolean;
}

// Only 3 chart types in the visible switcher — covers 95% of use cases
const CHART_SWITCHER: { key: ChartPanelChartType; label: string }[] = [
  { key: 'bar',   label: 'Bar'   },
  { key: 'line',  label: 'Line'  },
  { key: 'donut', label: 'Donut' },
];

const WIDGET_TYPE_OPTIONS: { key: WidgetType; icon: string; label: string }[] = [
  { key: 'kpi',    icon: 'SpeedHigh',    label: 'KPI'    },
  { key: 'number', icon: 'NumberSymbol', label: 'Number' },
  { key: 'chart',  icon: 'BarChart4',    label: 'Chart'  },
  { key: 'table',  icon: 'Table',        label: 'Table'  },
];

const SIZE_LABELS: Record<string, string> = { small: 'S', medium: 'M', large: 'L' };

const TREND: Record<string, { fg: string; bg: string; arrow: string }> = {
  up:   { fg: '#059669', bg: '#ECFDF5', arrow: '↑' },
  down: { fg: '#DC2626', bg: '#FFF1F1', arrow: '↓' },
  flat: { fg: '#6B7280', bg: '#F3F4F6', arrow: '→' },
};

/**
 * Hero-value colour. The governed dashboards (People Insights) render KPI
 * values in near-black by default and reserve the accent colour for deliberate
 * emphasis — so only colour the value when the widget explicitly carries a
 * colour scheme.
 */
function valueColor(widget: DashboardWidget): string {
  return widget.colorScheme?.[0] ?? '#1A1A1A';
}

function humanizeColumn(col: string): string {
  return col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Keyframes injected once ────────────────────────────────────────────────
const KEYFRAMES = `
  @keyframes wFadeUp {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const s = mergeStyleSets({
  // ── Base card ──────────────────────────────────────────────────────────────
  card: {
    background: '#FFFFFF',
    borderRadius: 10,
    // Exact card chrome of the governed dashboards (People Insights):
    // #E1E1E1 hairline + one soft ambient shadow, no hover lift.
    border: '1px solid #E1E1E1',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    overflow: 'visible',
    transition: 'box-shadow 0.2s ease',
    animation: 'wFadeUp 0.3s cubic-bezier(0.16,1,0.3,1)',
    position: 'relative' as const,
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    selectors: {
      ':hover': {
        boxShadow: '0 3px 12px rgba(0,0,0,0.09)',
      },
      ':hover .wc': { opacity: 1 },
    },
  },
  // Hover-reveal for editing chrome (chart-type switcher etc.) so cards read
  // as clean dashboard cards until the user reaches for them.
  hoverFade: {
    opacity: 0,
    transition: 'opacity 0.15s ease',
  },

  // ── Hover-reveal controls ──────────────────────────────────────────────────
  ctrlWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    opacity: 0,
    transition: 'opacity 0.15s ease',
    flexShrink: 0,
  },
  ctrlBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 4,
    padding: '3px 6px',
    fontSize: 10,
    fontWeight: 700,
    color: '#9CA3AF',
    lineHeight: '1',
    transition: 'all 0.1s ease',
    selectors: { ':hover': { background: '#F3F4F6', color: '#374151' } },
  },
  ctrlBtnOn: { background: '#D1FAE5', color: '#065F46' },
  ctrlRemove: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 4,
    padding: '3px 5px',
    color: '#D1D5DB',
    lineHeight: '1',
    transition: 'all 0.1s ease',
    selectors: { ':hover': { background: '#FEE2E2', color: '#DC2626' } },
  },
  ctrlSep: {
    width: 1,
    height: 12,
    background: '#E5E7EB',
    margin: '0 3px',
    flexShrink: 0,
  },
  drag: {
    cursor: 'grab',
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 3px',
    color: '#D1D5DB',
    flexShrink: 0,
    transition: 'color 0.1s',
    selectors: {
      ':hover': { color: '#9CA3AF' },
      ':active': { cursor: 'grabbing' },
    },
  },

  // ── Chart cards ────────────────────────────────────────────────────────────
  chartHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // No divider — the governed dashboards run the title straight into the
    // chart (section title + chart in one card).
    padding: '16px 20px 2px',
    flexShrink: 0,
    gap: 10,
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1A1A1A',
    letterSpacing: '-0.01em',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    flex: 1,
    minWidth: 0,
  },
  chartBody: {
    padding: '8px 12px 10px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: 0,
  },

  // ── Table ──────────────────────────────────────────────────────────────────
  tblHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px 8px',
    borderBottom: '1px solid #F0F1F3',
    flexShrink: 0,
  },
  tblTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1A1A1A',
    letterSpacing: '-0.01em',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  tblScroll: { overflowY: 'auto' as const, overflowX: 'auto' as const, flex: 1 },
  tbl: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 11.5 },
  th: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
    background: '#F8F9FA',
    padding: '7px 12px',
    textAlign: 'left' as const,
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    color: '#9CA3AF',
    borderBottom: '2px solid #E9EAEC',
    whiteSpace: 'nowrap' as const,
  },
  thNum: { textAlign: 'right' as const },
  td: {
    padding: '7px 12px',
    borderBottom: '1px solid #F3F4F6',
    color: '#374151',
    verticalAlign: 'middle' as const,
    selectors: {
      'tr:hover &': { background: '#F9FAFB' },
      'tr:last-child &': { borderBottom: 'none' },
    },
  },
  tdNum: {
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
    color: '#0D1117',
    letterSpacing: '-0.01em',
  },
  tdEven: { background: '#FCFCFD' },

  // ── Legend editor ──────────────────────────────────────────────────────────
  legendWrap: { padding: '8px 16px 12px', borderTop: '1px solid #F0F0F0', background: '#FAFAFA' },
  legendRow:  { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  legendInput: {
    flex: 1, fontSize: 12, padding: '4px 8px',
    border: '1px solid #E1E1E1', borderRadius: 4,
    outline: 'none', color: '#0D1117',
    selectors: { ':focus': { borderColor: '#2e873d' } },
  } as any,
  legendDot:  { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  legendSave: {
    padding: '4px 12px', fontSize: 11, fontWeight: 600,
    border: 'none', borderRadius: 4,
    background: '#2e873d', color: '#fff', cursor: 'pointer', marginTop: 4,
  },
});

// ── Shared controls component ──────────────────────────────────────────────
function Controls({
  widget, onRemove, onResize, onChangeWidgetType,
}: {
  widget: DashboardWidget;
  onRemove: (id: string) => void;
  onResize: (id: string, size: 'small' | 'medium' | 'large') => void;
  onChangeWidgetType?: (id: string, newType: WidgetType) => void;
}) {
  return (
    <div className={`${s.ctrlWrap} wc`}>
      {onChangeWidgetType && (
        <>
          {WIDGET_TYPE_OPTIONS.map((o) => (
            <TooltipHost key={o.key} content={o.label}>
              <button
                className={`${s.ctrlBtn} ${widget.type === o.key ? s.ctrlBtnOn : ''}`}
                onClick={() => onChangeWidgetType(widget.id, o.key)}
              >
                <Icon iconName={o.icon} style={{ fontSize: 10 }} />
              </button>
            </TooltipHost>
          ))}
          <div className={s.ctrlSep} />
        </>
      )}
      {(['small', 'medium', 'large'] as const).map((sz) => (
        <TooltipHost key={sz} content={`Size: ${sz}`}>
          <button
            className={`${s.ctrlBtn} ${widget.size === sz ? s.ctrlBtnOn : ''}`}
            onClick={() => onResize(widget.id, sz)}
          >
            {SIZE_LABELS[sz]}
          </button>
        </TooltipHost>
      ))}
      <button className={s.ctrlRemove} onClick={() => onRemove(widget.id)} title="Remove">
        <XIcon size={10} weight="bold" />
      </button>
    </div>
  );
}

const DashboardWidgetCard = forwardRef<HTMLDivElement, Props>(function DashboardWidgetCard(
  { widget, onRemove, onResize, onChangeChartType, onChangeWidgetType, onUpdateLegendLabels, readOnly, className, style, children, ...rest },
  ref,
) {
  const [editingLegend, setEditingLegend] = useState(false);
  const [legendDraft, setLegendDraft] = useState<Record<string, string>>({});
  const ac = valueColor(widget);
  const currentChartType: ChartPanelChartType = widget.chartType || 'bar';

  // Amber badge shown when the active dashboard filter(s) could not be applied
  // to this widget's query — it is displaying UNFILTERED data.
  const unfilteredBadge = widget.filterNotApplied ? (
    <TooltipHost content="The dashboard filter couldn't be applied to this widget — it is showing unfiltered data.">
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em',
        textTransform: 'uppercase', color: '#92400E',
        background: '#FEF3C7', border: '1px solid #FDE68A',
        borderRadius: 4, padding: '2px 6px', marginLeft: 8, flexShrink: 0,
        cursor: 'default',
      }}>
        Unfiltered
      </span>
    </TooltipHost>
  ) : null;

  // ── KPI ───────────────────────────────────────────────────────────────────
  // Styled to match the governed dashboards' KPI cards (see People Insights):
  // clean white card, quiet uppercase label, hero value in the accent colour.
  if (widget.type === 'kpi') {
    const trend = TREND[widget.kpiTrend || 'flat'];
    return (
      <div
        {...rest}
        ref={ref}
        className={`${s.card} ${className || ''}`}
        style={style}
        data-widget-id={widget.id}
      >
        <style>{KEYFRAMES}</style>
        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: 0 }}>
          {/* Top row doubles as the drag handle — grab anywhere except the controls */}
          <div
            className={!readOnly ? 'widget-drag-handle' : undefined}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, minHeight: 16, cursor: readOnly ? undefined : 'grab', userSelect: readOnly ? undefined : 'none' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
                textTransform: 'uppercase', color: '#9CA3AF',
                lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {widget.kpiLabel || widget.title}
              </div>
              {unfilteredBadge}
            </div>
            {!readOnly && (
              <div className="widget-controls" style={{ display: 'flex', alignItems: 'center' }}>
                <Controls widget={widget} onRemove={onRemove} onResize={onResize} onChangeWidgetType={onChangeWidgetType} />
              </div>
            )}
          </div>
          {/* Value — the hero element, accent-coloured like the governed dashboards */}
          <TooltipHost content={
            typeof widget.kpiValue === 'number'
              ? widget.kpiValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
              : String(widget.kpiValue ?? '')
          }>
            <div style={{
              fontSize: 36,
              fontWeight: 800,
              color: ac,
              letterSpacing: '-0.03em',
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              wordBreak: 'break-all',
            }}>
              {formatCompact(widget.kpiValue)}
            </div>
          </TooltipHost>
          {/* Sub-caption: widget title when the label carries the heading; trend pill only when directional */}
          {widget.kpiLabel && widget.kpiLabel !== widget.title && (
            <div style={{ marginTop: 4, fontSize: 12, color: '#9CA3AF', lineHeight: 1.4 }}>
              {widget.title}
            </div>
          )}
          {(widget.kpiTrend === 'up' || widget.kpiTrend === 'down') && (
            <div style={{
              marginTop: 8,
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 10, fontWeight: 800,
              padding: '3px 8px', borderRadius: 4,
              background: trend.bg, color: trend.fg,
              width: 'fit-content', letterSpacing: '0.02em',
            }}>
              <span style={{ fontSize: 11 }}>{trend.arrow}</span>
              <span>vs previous</span>
            </div>
          )}
        </div>
        {children}
      </div>
    );
  }

  // ── Number ────────────────────────────────────────────────────────────────
  if (widget.type === 'number') {
    return (
      <div
        {...rest}
        ref={ref}
        className={`${s.card} ${className || ''}`}
        style={{ ...style, borderTop: `3px solid ${widget.colorScheme?.[0] ?? '#E1E1E1'}` }}
        data-widget-id={widget.id}
      >
        <style>{KEYFRAMES}</style>
        {/* Controls float top-right on hover */}
        {!readOnly && (
          <div className="wc widget-controls" style={{
            position: 'absolute', top: 10, right: 10, zIndex: 2,
            display: 'flex', alignItems: 'center', gap: 4,
            opacity: 0, transition: 'opacity 0.15s',
          }}>
            <Controls widget={widget} onRemove={onRemove} onResize={onResize} onChangeWidgetType={onChangeWidgetType} />
          </div>
        )}
        <div
          className={!readOnly ? 'widget-drag-handle' : undefined}
          style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', padding: '16px 20px 20px',
            cursor: readOnly ? undefined : 'grab',
            userSelect: readOnly ? undefined : 'none',
          }}>

          <TooltipHost content={
            typeof widget.kpiValue === 'number'
              ? widget.kpiValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
              : String(widget.kpiValue ?? '')
          }>
            <div style={{
              fontSize: 48, fontWeight: 900, color: ac,
              letterSpacing: '-0.04em', lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {formatCompact(widget.kpiValue)}
            </div>
          </TooltipHost>
          <div style={{
            marginTop: 10, fontSize: 9.5, fontWeight: 800,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: '#9CA3AF',
          }}>
            {widget.title}
          </div>
          {unfilteredBadge && <div style={{ marginTop: 6 }}>{unfilteredBadge}</div>}
        </div>
        {children}
      </div>
    );
  }

  // ── Table ─────────────────────────────────────────────────────────────────
  if (widget.type === 'table') {
    const fields = widget.chartData?.fields || [];
    const rows   = widget.chartData?.rows   || [];
    // Detect numeric columns from first few rows
    const numericCols = new Set(
      fields.filter((f) =>
        (rows.slice(0, 5)).every((r) => r[f.name] == null || r[f.name] === '' || isNumericValue(r[f.name]))
        && rows.some((r) => r[f.name] != null && r[f.name] !== '')
      ).map((f) => f.name)
    );
    return (
      <div
        {...rest}
        ref={ref}
        className={`${s.card} ${className || ''}`}
        style={style}
        data-widget-id={widget.id}
      >
        <style>{KEYFRAMES}</style>
        <div className={`${s.tblHead} ${!readOnly ? 'widget-drag-handle' : ''}`} style={{ cursor: readOnly ? undefined : 'grab', userSelect: readOnly ? undefined : 'none' }}>
          {!readOnly && <span className={s.drag}><DotsSixVertical size={12} /></span>}
          <span className={s.tblTitle}>{widget.title}</span>
          {unfilteredBadge}
          {!readOnly && (
            <div className="widget-controls" style={{ display: 'flex', alignItems: 'center' }}>
              <Controls widget={widget} onRemove={onRemove} onResize={onResize} onChangeWidgetType={onChangeWidgetType} />
            </div>
          )}
        </div>
        <div className={s.tblScroll}>
          {rows.length > 0 ? (
            <table className={s.tbl}>
              <thead>
                <tr>
                  {fields.map((f) => (
                    <th key={f.name} className={`${s.th} ${numericCols.has(f.name) ? s.thNum : ''}`}>
                      {humanizeColumn(f.name)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    {fields.map((f) => {
                      const isNum = numericCols.has(f.name);
                      return (
                        <td
                          key={f.name}
                          className={`${s.td} ${isNum ? s.tdNum : ''} ${i % 2 === 1 ? s.tdEven : ''}`}
                        >
                          {row[f.name] != null ? String(row[f.name]) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 12 }}>
              No data loaded
            </div>
          )}
        </div>
        {children}
      </div>
    );
  }

  // ── Chart (default) ───────────────────────────────────────────────────────
  return (
    <div
      {...rest}
      ref={ref}
      className={`${s.card} ${className || ''}`}
      style={style}
      data-widget-id={widget.id}
    >
      <style>{KEYFRAMES}</style>
      <div className={`${s.chartHead} ${!readOnly ? 'widget-drag-handle' : ''}`} style={{ cursor: readOnly ? undefined : 'grab', userSelect: readOnly ? undefined : 'none' }}>
        {/* Left: drag + title — the whole header doubles as the drag handle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flex: 1 }}>
          {!readOnly && (
            <span className={s.drag}><DotsSixVertical size={12} weight="regular" /></span>
          )}
          <span className={s.chartTitle}>{widget.title}</span>
          {unfilteredBadge}
        </div>
        {/* Right: compact dark-pill type switcher + controls (excluded from drag) */}
        <div className="widget-controls" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {/* Dark-pill chart type switcher — hover-reveal so cards read clean like the governed dashboards */}
          <div className={`wc ${s.hoverFade}`} style={{ display: 'flex', gap: 1, background: '#F3F4F6', borderRadius: 6, padding: 3 }}>
            {CHART_SWITCHER.map((opt) => {
              const active = currentChartType === opt.key
                // Also highlight 'bar' when type is 'area' (fallback display)
                || (opt.key === 'bar' && currentChartType === 'area')
                || (opt.key === 'donut' && currentChartType === 'pie');
              return (
                <button
                  key={opt.key}
                  onClick={() => onChangeChartType(widget.id, opt.key)}
                  style={{
                    padding: '3px 9px',
                    borderRadius: 4, border: 'none',
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.015em',
                    background: active ? '#0D1117' : 'transparent',
                    color: active ? '#FFFFFF' : '#9CA3AF',
                    cursor: 'pointer',
                    transition: 'background 0.12s, color 0.12s',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {/* Legend pencil */}
          {onUpdateLegendLabels && (
            <TooltipHost content="Rename series">
              <button
                className={`${s.ctrlBtn} wc ${s.hoverFade}`}
                style={{ background: editingLegend ? '#D1FAE5' : undefined, color: editingLegend ? '#065F46' : undefined }}
                onClick={() => { if (!editingLegend) setLegendDraft(widget.legendLabels || {}); setEditingLegend((v) => !v); }}
              >
                <PencilSimple size={10} weight="regular" />
              </button>
            </TooltipHost>
          )}
          {!readOnly && <Controls widget={widget} onRemove={onRemove} onResize={onResize} onChangeWidgetType={onChangeWidgetType} />}
        </div>
      </div>
      <div className={s.chartBody}>
        <ErrorBoundary compact label={widget.title}>
          {widget.chartData ? (
            <div style={{ flex: 1, minHeight: 0 }}>
              <ChartPanel
                data={widget.chartData}
                chartType={currentChartType}
                height={widget.size === 'small' ? 200 : widget.size === 'large' ? 400 : 285}
                palette={widget.colorScheme}
                legendLabels={widget.legendLabels}
                embedded
              />
            </div>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 12.5 }}>
              No data loaded
            </div>
          )}
        </ErrorBoundary>
      </div>
      {/* Legend editor */}
      {editingLegend && widget.chartData?.fields && widget.chartData?.rows && onUpdateLegendLabels && (() => {
        const numCols = (widget.chartData!.fields || [])
          .filter((f) => {
            const vals = (widget.chartData!.rows || []).slice(0, 5).map((r) => r[f.name]);
            return vals.some((v) => typeof v === 'number' || (!isNaN(Number(String(v).replace(/[$,]/g, ''))) && String(v).trim() !== ''));
          })
          .map((f) => f.name);
        const pal = widget.colorScheme || ['#2e873d', '#55a655', '#7BC47B'];
        return (
          <div className={s.legendWrap}>
            {numCols.map((col, i) => (
              <div key={col} className={s.legendRow}>
                <div className={s.legendDot} style={{ background: pal[i % pal.length] }} />
                <input
                  className={s.legendInput}
                  value={legendDraft[col] ?? humanizeColumn(col)}
                  onChange={(e) => setLegendDraft((prev) => ({ ...prev, [col]: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { onUpdateLegendLabels(widget.id, legendDraft); setEditingLegend(false); }
                  }}
                />
              </div>
            ))}
            <button className={s.legendSave} onClick={() => { onUpdateLegendLabels(widget.id, legendDraft); setEditingLegend(false); }}>
              Apply
            </button>
          </div>
        );
      })()}
      {children}
    </div>
  );
});

export default DashboardWidgetCard;
